'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    ReactFlow,
    Background,
    Controls,
    MarkerType,
    Position,
    useReactFlow,
    ReactFlowProvider,
    useStore,
    getNodesBounds,
    getSmoothStepPath,
    BaseEdge,
    EdgeLabelRenderer,
} from '@xyflow/react';
import classNames from 'classnames';
import ELK from 'elkjs/lib/elk.bundled.js';
import { toPng } from 'html-to-image';
import jsPDF from 'jspdf';
import '@xyflow/react/dist/style.css';

import OrganizationChartV2Node, {
    avatarOverflow,
} from './OrganizationChartV2Node';
import s from './OrganizationChartV2.module.scss';

const elk = new ELK();

const NODE_WIDTH = 240;

const EDGE_COLOR = '#e0e0e0';
const CANVAS_COLOR = '#f5f5f5';
const EDGE_RADIUS = 20;
const DOT_COLOR = '#eeeeee';

const CARD_PAD = 16;
const CARD_GAP = 8;
const NAME_LH = 17;
const META_LH = 16;
const AVATAR = 22;
const META_TOP = 3;
const BUTTON = 28;
const COUNTRY_ROW = 19;
// Average glyph width at the card's two type sizes, and the room the avatar
// cluster takes off the meta row. The card wraps at whatever is left, so the
// height estimate below follows NODE_WIDTH rather than a tuned line length.
const NAME_CHAR_W = 10.2;
const META_CHAR_W = 7.7;
const AVATARS_W = 77;
const AVATARS_OVERFLOW_W = 100;

const CARD_INNER = NODE_WIDTH - CARD_PAD;
const NAME_CHARS = Math.floor(CARD_INNER / NAME_CHAR_W);

const wrapLines = (len, perLine) => Math.max(1, Math.ceil(len / perLine));

const nodeHeight = (node) => {
    const nameLines = wrapLines((node.name || '').length, NAME_CHARS);
    const headerH = Math.max(nameLines * NAME_LH, COUNTRY_ROW);

    const meta = [
        node.revenue && parseFloat(node.revenue) ? 12 : 0,
        node.employees && parseFloat(node.employees) ? 13 : 0,
        node.incorporated ? 9 : 0,
    ].filter(Boolean);
    const people = node.relatedIndividuals || [];
    const overflow = avatarOverflow(people);
    const hasCircles = people.some((i) => i.src) || overflow > 0;
    const metaChars =
        meta.reduce((a, b) => a + b, 0) + Math.max(0, meta.length - 1) * 3;
    const metaWidth =
        CARD_INNER -
        (hasCircles ? (overflow > 0 ? AVATARS_OVERFLOW_W : AVATARS_W) : 0);
    const perLine = Math.floor(metaWidth / META_CHAR_W);
    const metaLines = meta.length ? wrapLines(metaChars, perLine) : 0;
    const metaH = hasCircles
        ? Math.max(AVATAR, META_TOP + metaLines * META_LH)
        : metaLines * META_LH;

    const buttons = node.hasToggle ? 2 : 1;
    const buttonsH = buttons * BUTTON + (buttons - 1) * CARD_GAP;

    return CARD_PAD + headerH + CARD_GAP + metaH + CARD_GAP + buttonsH;
};

const INITIAL_ZOOM = 1;

const EXPORT_PADDING = 40;
const EXPORT_SCALE = 2;
// Beyond this the canvas silently comes back blank in Safari and Chrome.
const EXPORT_MAX_PX = 8000;

const EXPORT_LAYOUT_TIMEOUT = 10000;

const waitUntil = async (predicate) => {
    const deadline = performance.now() + EXPORT_LAYOUT_TIMEOUT;
    while (!predicate()) {
        if (performance.now() > deadline) return false;
        await new Promise((resolve) => window.requestAnimationFrame(resolve));
    }
    return true;
};

// The view each chart was left in — which branches were open and where the
// reader had panned and zoomed to — so a trip to another tab and back does not
// throw away the exploring they had done. Held in memory rather than in storage
// on purpose: it is meant to last the visit, and a reload or a new sign-in
// starts from the company again.
const savedViews = new Map();

const reportExportFailure = (error) => {
    console.warn('Could not export the organisation chart:', error);
    return null;
};

const elkOptions = {
    'elk.algorithm': 'layered',
    'elk.direction': 'DOWN',
    'elk.layered.spacing.nodeNodeBetweenLayers': '80',
    'elk.spacing.nodeNode': '40',
    'elk.edgeRouting': 'ORTHOGONAL',
    'elk.layered.nodePlacement.strategy': 'BRANDES_KOEPF',
    'elk.layered.nodePlacement.bk.fixedAlignment': 'BALANCED',
    'elk.layered.considerModelOrder.strategy': 'NODES_AND_EDGES',
};

const nodeTypes = { orgNode: OrganizationChartV2Node };

const EDGE_LABEL_GAP = 6;

// The built-in label sits on the path's midpoint, which puts it right on top of
// the horizontal run of a fan-out. This one is lifted clear of the line.
const OrganizationChartV2Edge = ({
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    label,
    markerEnd,
    style,
    data,
}) => {
    const [path, labelX, labelY] = getSmoothStepPath({
        sourceX,
        sourceY,
        sourcePosition,
        targetX,
        targetY,
        targetPosition,
        borderRadius: EDGE_RADIUS,
    });

    return (
        <>
            <BaseEdge id={id} path={path} markerEnd={markerEnd} style={style} />
            {label && (
                <EdgeLabelRenderer>
                    <div
                        className={classNames(s.EdgeLabel, {
                            [s.EdgeEnter]: data?.isEntering,
                        })}
                        style={{
                            transform: `translate(-50%, -100%) translate(${labelX}px, ${labelY - EDGE_LABEL_GAP}px)`,
                        }}>
                        {label}
                    </div>
                </EdgeLabelRenderer>
            )}
        </>
    );
};

const edgeTypes = { orgEdge: OrganizationChartV2Edge };

const indexGraph = (rawNodes) => {
    const fullById = {};
    const childrenOf = {};
    const relations = [];
    const rootIds = new Set();

    rawNodes.forEach((entry) => {
        const existing = fullById[entry.id];
        if (!existing || entry.networkChildId !== undefined) {
            fullById[entry.id] = { ...existing, ...entry };
        }

        if (entry.parent) {
            relations.push({
                parent: entry.parent,
                child: entry.id,
                text: entry.annotationOnLinkToParent?.text,
            });
            (childrenOf[entry.parent] ||= []).push(entry.id);
        } else if (entry.isRoot || entry.parent === undefined) {
            rootIds.add(entry.id);
        }
    });

    const hasParent = new Set(relations.map((r) => r.child));
    Object.keys(fullById).forEach((id) => {
        if (!hasParent.has(id)) rootIds.add(id);
    });

    return { fullById, childrenOf, relations, rootIds: [...rootIds] };
};

const buildElements = (graph, revealed, activeId, onToggle, seen) => {
    const isEntering = (id) => !seen || !seen.has(id);

    const { fullById, relations, childrenOf } = graph;

    const nodes = Object.keys(fullById)
        .filter((id) => revealed.has(id))
        .map((id) => {
            const node = fullById[id];
            const children = childrenOf[id] || [];
            const hasToggle = children.length > 0;
            return {
                id,
                type: 'orgNode',
                position: { x: 0, y: 0 },
                width: NODE_WIDTH,
                height: nodeHeight({ ...node, hasToggle }),
                sourcePosition: Position.Bottom,
                targetPosition: Position.Top,
                data: {
                    ...node,
                    isActive: id === activeId,
                    hasToggle,
                    hiddenChildren: children.filter((c) => !revealed.has(c))
                        .length,
                    isExpanded:
                        hasToggle && children.every((c) => revealed.has(c)),
                    isEntering: isEntering(id),
                    onToggle: () => onToggle(id),
                },
            };
        });

    const edges = relations
        .filter((r) => revealed.has(r.parent) && revealed.has(r.child))
        .map((r) => ({
            id: `${r.parent}-${r.child}`,
            className: isEntering(`${r.parent}-${r.child}`)
                ? s.EdgeEnter
                : undefined,
            source: r.parent,
            target: r.child,
            type: 'orgEdge',
            label: r.text,
            data: { isEntering: isEntering(`${r.parent}-${r.child}`) },
            style: { stroke: EDGE_COLOR, strokeWidth: 1 },
            markerEnd: {
                type: MarkerType.ArrowClosed,
                color: EDGE_COLOR,
                width: 14,
                height: 14,
            },
        }));

    return { nodes, edges };
};

const elkLayout = async (nodes, edges) => {
    const graph = {
        id: 'root',
        layoutOptions: elkOptions,
        children: nodes.map((n) => ({
            id: n.id,
            width: n.width,
            height: n.height,
        })),
        edges: edges.map((e) => ({
            id: e.id,
            sources: [e.source],
            targets: [e.target],
        })),
    };

    const laid = await elk.layout(graph);
    const positions = Object.fromEntries(
        laid.children.map((c) => [c.id, { x: c.x, y: c.y }])
    );

    return nodes.map((n) => ({
        ...n,
        position: positions[n.id] || { x: 0, y: 0 },
    }));
};

const ancestorsOf = (id, graph) => {
    const parentsByChild = {};
    graph.relations.forEach((r) => {
        (parentsByChild[r.child] ||= []).push(r.parent);
    });
    const chain = new Set();
    const queue = [...(parentsByChild[id] || [])];
    while (queue.length) {
        const p = queue.shift();
        if (chain.has(p)) continue;
        chain.add(p);
        (parentsByChild[p] || []).forEach((gp) => queue.push(gp));
    }
    return chain;
};

const descendantsOf = (id, graph) => {
    const found = new Set();
    const queue = [...(graph.childrenOf[id] || [])];
    while (queue.length) {
        const child = queue.shift();
        if (found.has(child)) continue;
        found.add(child);
        (graph.childrenOf[child] || []).forEach((next) => queue.push(next));
    }
    return found;
};

const Flow = ({
    nodes: rawNodes = [],
    activeId,
    initialReveal = [],
    exportRef = null,
    fileName = 'organization-chart',
}) => {
    const { fitView, setCenter, getNodes, getViewport, setViewport } =
        useReactFlow();
    const rootRef = useRef(null);
    const paneWidth = useStore((state) => state.width);
    const paneHeight = useStore((state) => state.height);

    const graph = useMemo(() => indexGraph(rawNodes), [rawNodes]);

    const viewKey = activeId || fileName;
    // Read once, at mount: what the chart looked like when it was last left.
    const restored = useRef(savedViews.get(viewKey));

    const [revealed, setRevealed] = useState(() => {
        if (restored.current?.revealed) {
            return new Set(restored.current.revealed);
        }

        const init = new Set();
        const expandFrom = activeId
            ? [...ancestorsOf(activeId, graph), activeId]
            : graph.rootIds;

        expandFrom.forEach((id) => {
            init.add(id);
            (graph.childrenOf[id] || []).forEach((child) => init.add(child));
        });

        initialReveal.forEach((id) => init.add(id));
        return init;
    });

    const rememberView = useCallback(
        (view) =>
            savedViews.set(viewKey, { ...savedViews.get(viewKey), ...view }),
        [viewKey]
    );

    useEffect(() => {
        rememberView({ revealed: [...revealed] });
    }, [rememberView, revealed]);

    const [positions, setPositions] = useState(null);
    const positionsRef = useRef(null);
    const centred = useRef(null);

    useEffect(() => {
        positionsRef.current = positions;
    }, [positions]);

    // The node a reader just opened or closed, and where it sat when they did.
    // Collapsing a branch narrows the tree, and elk re-centres what is left over
    // it — so without this the node they clicked slides away under their
    // cursor, which for a root with four children is most of a screen.
    const anchor = useRef(null);

    const toggle = useCallback(
        (id) => {
            anchor.current = {
                id,
                position: positionsRef.current?.[id] || null,
            };

            setRevealed((prev) => {
                const children = graph.childrenOf[id] || [];
                const next = new Set(prev);

                if (children.every((child) => prev.has(child))) {
                    descendantsOf(id, graph).forEach((descendant) =>
                        next.delete(descendant)
                    );
                } else {
                    children.forEach((child) => next.add(child));
                }

                return next;
            });
        },
        [graph]
    );

    const seen = useRef(null);

    const { nodes: builtNodes, edges } = useMemo(
        () => buildElements(graph, revealed, activeId, toggle, seen.current),
        [graph, revealed, activeId, toggle]
    );

    useEffect(() => {
        seen.current = new Set([
            ...builtNodes.map((n) => n.id),
            ...edges.map((e) => e.id),
        ]);
    }, [builtNodes, edges]);

    useEffect(() => {
        let cancelled = false;
        const { nodes: layoutNodes, edges: layoutEdges } = buildElements(
            graph,
            revealed,
            activeId,
            () => {}
        );
        elkLayout(layoutNodes, layoutEdges).then((laidNodes) => {
            if (cancelled) return;
            setPositions(
                Object.fromEntries(laidNodes.map((n) => [n.id, n.position]))
            );
        });
        return () => {
            cancelled = true;
        };
    }, [graph, revealed, activeId]);

    const nodes = useMemo(
        () =>
            positions
                ? builtNodes.map((n) => ({
                      ...n,
                      position: positions[n.id] || { x: 0, y: 0 },
                  }))
                : [],
        [builtNodes, positions]
    );

    // Move the viewport by however far the toggled node moved, so it stays
    // where the reader left it and the rest of the tree opens or closes around
    // it.
    useEffect(() => {
        const held = anchor.current;

        if (!held || !positions) {
            return;
        }

        anchor.current = null;

        const before = held.position;
        const after = positions[held.id];

        if (!before || !after) {
            return;
        }

        const dx = after.x - before.x;
        const dy = after.y - before.y;

        if (!dx && !dy) {
            return;
        }

        const { x, y, zoom } = getViewport();

        setViewport({ x: x - dx * zoom, y: y - dy * zoom, zoom });
    }, [positions, getViewport, setViewport]);

    useEffect(() => {
        if (!positions || !paneWidth || !paneHeight) return;
        if (centred.current === graph) return;
        centred.current = graph;

        // A chart the reader has already explored opens where they left it,
        // rather than snapping back to the company it is about.
        const held = restored.current?.viewport;

        if (held) {
            restored.current = null;
            setViewport(held);
            return;
        }

        const active = nodes.find((n) => n.id === activeId);
        const zoom = Math.min(INITIAL_ZOOM, paneWidth / (NODE_WIDTH * 1.15));
        window.requestAnimationFrame(() => {
            if (active) {
                setCenter(
                    active.position.x + NODE_WIDTH / 2,
                    active.position.y + active.height / 2,
                    { zoom, duration: 0 }
                );
            } else {
                fitView({ duration: 300, padding: 0.2 });
            }
        });
    }, [
        positions,
        nodes,
        graph,
        activeId,
        paneWidth,
        paneHeight,
        setCenter,
        setViewport,
        fitView,
    ]);

    // The viewport holds the nodes and edges but not the zoom controls or the
    // dotted background, so capturing it rather than the pane keeps the chrome
    // out of the file. Its transform is overridden for the capture alone, which
    // leaves the live chart untouched.
    const snapshotViewport = useCallback(
        async (viewport) => {
            const bounds = getNodesBounds(getNodes());
            if (!bounds.width || !bounds.height) return null;

            const width = Math.min(
                bounds.width + EXPORT_PADDING * 2,
                EXPORT_MAX_PX
            );
            const height = Math.min(
                bounds.height + EXPORT_PADDING * 2,
                EXPORT_MAX_PX
            );

            const dataUrl = await toPng(viewport, {
                backgroundColor: CANVAS_COLOR,
                pixelRatio: EXPORT_SCALE,
                width,
                height,
                // html-to-image waits for every cloned image to load and
                // rejects the whole export on the first one that cannot, so a
                // single missing portrait would otherwise produce no file.
                filter: (node) =>
                    !(node.tagName === 'IMG' && node.naturalWidth === 0),
                style: {
                    width: `${width}px`,
                    height: `${height}px`,
                    transform: `translate(${EXPORT_PADDING - bounds.x}px, ${EXPORT_PADDING - bounds.y}px) scale(1)`,
                    transformOrigin: '0 0',
                },
            });

            return { dataUrl, width, height };
        },
        [getNodes]
    );

    // The export shows the whole tree, not just the branches the reader happens
    // to have opened, so everything is revealed first and the previous state is
    // put back afterwards. Revealing triggers a fresh elk layout, hence the
    // wait: capturing earlier catches the new nodes stacked at the origin.
    //
    // Everything the chart can reach, which is not everything the response
    // holds: a company's chart walks down from its own ancestors, so a second
    // root in the same payload — another owner's tree, sharing nothing with
    // this one — is a company no amount of clicking here would show, and no
    // company to hand someone in a picture of this network.
    const capture = useCallback(async () => {
        const root = rootRef.current;
        const viewport = root?.querySelector('.react-flow__viewport');
        if (!viewport) return null;

        const reachable = new Set(
            activeId
                ? [...ancestorsOf(activeId, graph), activeId]
                : graph.rootIds
        );
        [...reachable].forEach((id) =>
            descendantsOf(id, graph).forEach((descendant) =>
                reachable.add(descendant)
            )
        );

        const everyone = [...reachable];
        const previouslyRevealed = revealed;

        root.classList.add(s.Frozen);
        setRevealed(new Set(everyone));

        try {
            // Node positions land before react-flow has re-routed the
            // edges, so waiting on the nodes alone captures a chart with no
            // lines at all. Wait for the drawn paths to hold steady instead of
            // predicting how many there should be.
            let drawnLastFrame = -1;
            const laidOut = await waitUntil(() => {
                const current = getNodes();
                if (current.length !== everyone.length) return false;

                const positioned = current.every((n) => {
                    const laid = positionsRef.current?.[n.id];
                    return (
                        laid &&
                        laid.x === n.position.x &&
                        laid.y === n.position.y
                    );
                });
                if (!positioned) return false;

                const drawn = [
                    ...root.querySelectorAll('.react-flow__edge-path'),
                ].filter((p) => (p.getAttribute('d') || '').length > 0).length;
                const steady = drawn > 0 && drawn === drawnLastFrame;
                drawnLastFrame = drawn;

                return steady;
            });
            if (!laidOut) {
                throw new Error('the full chart did not finish laying out');
            }

            return await snapshotViewport(viewport);
        } finally {
            root.classList.remove(s.Frozen);
            setRevealed(previouslyRevealed);
        }
    }, [activeId, graph, revealed, getNodes, snapshotViewport]);

    const downloadImage = useCallback(async () => {
        const shot = await capture().catch(reportExportFailure);
        if (!shot) return;

        const link = document.createElement('a');
        link.href = shot.dataUrl;
        link.download = `${fileName}.png`;
        link.click();
    }, [capture, fileName]);

    const downloadPdf = useCallback(async () => {
        const shot = await capture().catch(reportExportFailure);
        if (!shot) return;

        const pdf = new jsPDF({
            orientation: shot.width >= shot.height ? 'landscape' : 'portrait',
            unit: 'pt',
            format: [shot.width, shot.height],
        });
        pdf.addImage(shot.dataUrl, 'PNG', 0, 0, shot.width, shot.height);
        pdf.save(`${fileName}.pdf`);
    }, [capture, fileName]);

    useEffect(() => {
        if (!exportRef) return undefined;

        exportRef.current = { downloadImage, downloadPdf };
        return () => {
            exportRef.current = null;
        };
    }, [exportRef, downloadImage, downloadPdf]);

    return (
        <div className={s.Root} ref={rootRef}>
            <ReactFlow
                nodes={nodes}
                edges={edges}
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
                minZoom={0.1}
                maxZoom={2.5}
                nodesDraggable={false}
                nodesConnectable={false}
                onMoveEnd={(_, viewport) => rememberView({ viewport })}
                proOptions={{ hideAttribution: true }}>
                <Background color={DOT_COLOR} gap={20} />
                <Controls showInteractive={false} />
            </ReactFlow>
        </div>
    );
};

const OrganizationChartV2 = (props) => (
    <ReactFlowProvider>
        <Flow {...props} />
    </ReactFlowProvider>
);

export default OrganizationChartV2;
